
import { defineComponent, PropType } from 'vue';

// Helpers
import { getProductsWithSortOrder } from './helpers/getProductsWithSortOrder/getProductsWithSortOrder';
import { getProductsWithFiltersApplied } from './helpers/getProductsWithFiltersApplied/getProductsWithFiltersApplied';
import { updateURLParams } from './helpers/updateURLParams/updateURLParams';
// Components
import ProductCard from './components/ProductCard/ProductCard.vue';
import FeaturedCard from './components/FeaturedCard/FeaturedCard.vue';
import ActiveFilters from './components/ActiveFilters/ActiveFilters.vue';
import FilterOptions from './components/FilterOptions/FilterOptions.vue';

// Models
import {
  SortByValues,
  Filter,
  FilterCategory,
  SortBy,
  ContentBlock,
  Product,
} from './CoffeePicker.model';
import { decodeURLParams, DecodeUrlParamsObj } from './helpers/decodeURLParams/decodeUrlParams';

export default defineComponent({
  props: {
    products: Array as PropType<Product[]>,
    contentBlocks: Array as PropType<ContentBlock[]>,
  },
  mounted() {
    // Construct filter options from product data
    this.populateFiltersFromProducts();

    // Set local products variable with value from props
    this.localProducts = this.products;

    // Set local content blocks with value from props
    this.localContentBlocks = this.contentBlocks;

    // Decode the URL
    const urlParams: DecodeUrlParamsObj = decodeURLParams();

    // Apply Sort from URL
    if (urlParams.sort) {
      this.currentSort = urlParams.sort;
    }

    // Apply filters from URL
    if (Object.keys(urlParams.filters).length) {
      // Loop and store the active filters.
      const activeFilters: Filter[] = [];
      this.filterCategories.forEach((category: FilterCategory) => {
        // Ignore if property is not in the URL params
        if (!urlParams.filters[category.property]) return;

        // Loop filters and add to active filters if active
        category.filters.forEach((filter) => {
          const formattedName = filter.name
            .replace(' ', '-')
            .toLocaleLowerCase();

          // If filter is active, add to active filters
          if (urlParams.filters[category.property].includes(formattedName)) {
            filter.active = true;
            activeFilters.push(filter);
          }
        });
      });

      // Update state and show filter bar
      this.isFilterActive = true;
      this.activeFilters = [...activeFilters];
    }
  },
  data() {
    return {
      filterCategories: [] as FilterCategory[],
      localProducts: [] as Product[],
      localContentBlocks: [] as ContentBlock[],
      currentSort: SortByValues[0],
      isFilterActive: false,
      sortOptions: SortByValues,
      currentPage: 1,
      perPage: 12,
    };
  },
  computed: {
    /**
     * Returns the current active filters
     */
    activeFilters(): Filter[] {
      const activeItems: Filter[] = [];
      this.filterCategories.forEach((category: FilterCategory) => {
        category.filters.forEach((filter: Filter) => {
          if (filter.active) {
            activeItems.push(filter);
          }
        });
      });
      return activeItems;
    },
    /**
     * Returns products filtered by current filters and sorted in selected sort order
     */
    gridItems(): Array<Product | ContentBlock> {
      // Edge case of no filter categories
      if (!this.filterCategories.length) {
        return this.localProducts;
      }

      // Apply Filters
      let filteredProds = getProductsWithFiltersApplied(
        this.products,
        this.filterCategories
      );

      // Apply Sort
      let sortedProducts: Array<Product | ContentBlock>;
      sortedProducts = getProductsWithSortOrder(
        filteredProds,
        this.currentSort
      );

      // Splice in content blocks
      for (const contentItem of (
        this.localContentBlocks as ContentBlock[]
      ).sort((x) => x.position)) {
        if (contentItem) {
          sortedProducts.splice(contentItem.position, 0, contentItem);
        }
      }

      // Store the new available items
      this.localProducts = sortedProducts;

      // Apply Paginated load more
      return sortedProducts.slice(0, this.perPage * this.currentPage);

      // return sortedProducts;
    },
    /**
     * Identifies if there is more items to render.
     */
    showLoadMore(): boolean {
      return this.localProducts.length / this.perPage > this.currentPage;
    },
  },
  methods: {
    /**
     * Toggles the filter picker panel open/closed.
     */
    openPickerFilter(): void {
      this.isFilterActive = !this.isFilterActive;
    },
    /**
     * Toggles when a filter option is selected
     */
    toggleFilterOption(filterOption: Filter): void {
      filterOption.active = !filterOption.active;
      updateURLParams(this.activeFilters, this.currentSort);
    },
    /**
     * Applies a new sort order.
     */
    handleSort(newSort: SortBy): void {
      this.currentSort = newSort;
      updateURLParams(this.activeFilters, this.currentSort);
    },
    /**
     * Create the available filters from the available products.
     */
    populateFiltersFromProducts(): void {
      const products: Product[] = this.products;

      // Create base filter categories
      const filterCategories: FilterCategory[] = [
        {
          name: 'Flavour',
          property: 'flavourProfile',
          filters: [],
        },
        {
          name: 'Type',
          property: 'type',
          filters: [],
        },
        {
          name: 'Process',
          property: 'process',
          filters: [],
        },
        {
          name: 'Origin',
          property: 'origin',
          filters: [],
        },
        {
          name: 'Varietal',
          property: 'varietal',
          filters: [],
        },
        {
          name: 'Roast',
          property: 'roast',
          filters: [],
        },
      ];

      // Polulate each category.
      filterCategories.forEach((category) => {
        // Get the unique values for the category
        const potentialValues = Array.from(
          new Set(products.map((product) => product[category.property]))
        ).filter((value) => value);

        // Create filter options for each value
        category.filters = potentialValues.map((value) => {
          return {
            name: value,
            property: category.property,
            active: false,
          };
        });
      });

      // Set filter categories
      this.filterCategories = filterCategories;
    },
    /**
     * Loads more items from the grid.
     */
    handleLoadMore(): void {
      this.currentPage++;
    },
  },
  components: {
    ProductCard,
    FeaturedCard,
    ActiveFilters,
    FilterOptions,
  },
});
